import React from 'react';

/**
 * NBA half-court SVG canvas with proportionally correct lines.
 * viewBox 0 0 1000 1000 — hoop at bottom (y≈880), midcourt line at top (y=20).
 * Real-court ref: 50 ft wide × 47 ft half-length. Scaled to ~1000×1000 (slight stretch).
 *
 * Coordinate constants (used also by PlayEditor inline court).
 */
export const COURT = {
  V: 1000,
  // sidelines / baseline / midcourt
  marginX: 20,
  marginYTop: 20,
  marginYBottom: 20,
  // hoop
  hoopX: 500,
  hoopY: 880,
  hoopR: 18,
  // backboard
  backboardX1: 435,
  backboardX2: 565,
  backboardY: 905,
  // paint (key) — 16 ft wide × 19 ft long
  paintX1: 340,
  paintX2: 660,
  paintTopY: 600,
  paintBottomY: 980,
  // free-throw circle
  ftCx: 500,
  ftCy: 600,
  ftR: 120,
  // 3pt geometry — corners straight, then arc, radius ≈22 ft from hoop
  threeR: 470,
  threeCornerX1: 80,
  threeCornerX2: 920,
  threeCornerY: 670, // where corner line meets arc (computed: hoopY - sqrt(R² - (corner-500)²))
  // restricted area arc r ≈ 4 ft
  restrictedR: 80,
  // center circle (mid-court) — only bottom half visible
  centerCx: 500,
  centerCy: 20,
  centerR: 120,
};

const G = '#D4AF37'; // gold
const G_FADE = 'rgba(212,175,55,0.55)';

/**
 * Stand-alone <g> with the court lines so PlayEditor can render them inside its own <svg>.
 */
export function CourtLines() {
  const C = COURT;
  return (
    <g pointerEvents="none">
      {/* Outer rectangle (sidelines + baseline + midcourt line) */}
      <rect x={C.marginX} y={C.marginYTop} width={C.V - 2 * C.marginX} height={C.V - C.marginYTop - C.marginYBottom}
        fill="none" stroke={G} strokeWidth="3" />

      {/* Mid-court center circle (only bottom half visible) */}
      <circle cx={C.centerCx} cy={C.centerCy} r={C.centerR} fill="none" stroke={G} strokeWidth="2.5" />

      {/* Paint (key) */}
      <rect x={C.paintX1} y={C.paintTopY} width={C.paintX2 - C.paintX1} height={C.paintBottomY - C.paintTopY}
        fill="none" stroke={G} strokeWidth="2.5" />

      {/* Free-throw line (top edge of paint) */}
      <line x1={C.paintX1} y1={C.paintTopY} x2={C.paintX2} y2={C.paintTopY} stroke={G} strokeWidth="2.5" />

      {/* Free-throw circle: top half solid, bottom half dashed */}
      <path d={`M ${C.ftCx - C.ftR} ${C.ftCy} A ${C.ftR} ${C.ftR} 0 0 1 ${C.ftCx + C.ftR} ${C.ftCy}`}
        fill="none" stroke={G} strokeWidth="2.5" />
      <path d={`M ${C.ftCx - C.ftR} ${C.ftCy} A ${C.ftR} ${C.ftR} 0 0 0 ${C.ftCx + C.ftR} ${C.ftCy}`}
        fill="none" stroke={G} strokeWidth="2" strokeDasharray="8 7" />

      {/* 3-point line: corners + arc (arc bulges toward midcourt = up) */}
      <line x1={C.threeCornerX1} y1={C.paintBottomY} x2={C.threeCornerX1} y2={C.threeCornerY} stroke={G} strokeWidth="2.5" />
      <line x1={C.threeCornerX2} y1={C.paintBottomY} x2={C.threeCornerX2} y2={C.threeCornerY} stroke={G} strokeWidth="2.5" />
      <path d={`M ${C.threeCornerX1} ${C.threeCornerY} A ${C.threeR} ${C.threeR} 0 0 1 ${C.threeCornerX2} ${C.threeCornerY}`}
        fill="none" stroke={G} strokeWidth="2.5" />

      {/* Backboard */}
      <line x1={C.backboardX1} y1={C.backboardY} x2={C.backboardX2} y2={C.backboardY} stroke={G} strokeWidth="4" />
      {/* Hoop */}
      <circle cx={C.hoopX} cy={C.hoopY} r={C.hoopR} fill="none" stroke={G} strokeWidth="3" />
      {/* Restricted area arc */}
      <path d={`M ${C.hoopX - C.restrictedR} ${C.hoopY} A ${C.restrictedR} ${C.restrictedR} 0 0 0 ${C.hoopX + C.restrictedR} ${C.hoopY}`}
        fill="none" stroke={G_FADE} strokeWidth="2" />

      {/* Center watermark */}
      <text x={C.V / 2} y={C.V / 2 + 10} textAnchor="middle"
        fontFamily="Oswald, sans-serif" fontSize="40" fill={G} opacity="0.05" fontWeight="700">
        PLAYMASTER
      </text>
    </g>
  );
}

/**
 * Full-court layout: two half-courts mirrored vertically with a center line
 * and jump circle between them. viewBox 0 0 1000 2000 (portrait).
 *
 *   y =    0 ──── opponent's baseline (top hoop ~y=120 in svg coords)
 *   y = 1000 ──── half-court line (centre jump circle)
 *   y = 2000 ──── your baseline (bottom hoop ~y=1880)
 *
 * Used by the Easy editor when the coach needs to draw a play that spans both
 * ends (press break, full-court press, transition).
 */
export function FullCourtLines() {
  const C = COURT;
  return (
    <g pointerEvents="none">
      {/* Outer rectangle covering the full court */}
      <rect x={C.marginX} y={C.marginYTop} width={C.V - 2 * C.marginX} height={2 * C.V - C.marginYTop - C.marginYBottom}
        fill="none" stroke={G} strokeWidth="3" />

      {/* === BOTTOM HALF (your offensive end — same as the regular half court, shifted down by 1000) === */}
      <g transform={`translate(0, ${C.V})`}>
        {/* Paint */}
        <rect x={C.paintX1} y={C.paintTopY - C.V + C.V} width={C.paintX2 - C.paintX1} height={C.paintBottomY - C.paintTopY}
          fill="none" stroke={G} strokeWidth="2.5" />
        {/* FT line */}
        <line x1={C.paintX1} y1={C.paintTopY} x2={C.paintX2} y2={C.paintTopY} stroke={G} strokeWidth="2.5" />
        {/* FT circle */}
        <path d={`M ${C.ftCx - C.ftR} ${C.ftCy} A ${C.ftR} ${C.ftR} 0 0 1 ${C.ftCx + C.ftR} ${C.ftCy}`} fill="none" stroke={G} strokeWidth="2.5" />
        <path d={`M ${C.ftCx - C.ftR} ${C.ftCy} A ${C.ftR} ${C.ftR} 0 0 0 ${C.ftCx + C.ftR} ${C.ftCy}`} fill="none" stroke={G} strokeWidth="2" strokeDasharray="8 7" />
        {/* 3-pt corners + arc */}
        <line x1={C.threeCornerX1} y1={C.paintBottomY} x2={C.threeCornerX1} y2={C.threeCornerY} stroke={G} strokeWidth="2.5" />
        <line x1={C.threeCornerX2} y1={C.paintBottomY} x2={C.threeCornerX2} y2={C.threeCornerY} stroke={G} strokeWidth="2.5" />
        <path d={`M ${C.threeCornerX1} ${C.threeCornerY} A ${C.threeR} ${C.threeR} 0 0 1 ${C.threeCornerX2} ${C.threeCornerY}`} fill="none" stroke={G} strokeWidth="2.5" />
        {/* Backboard + hoop + restricted */}
        <line x1={C.backboardX1} y1={C.backboardY} x2={C.backboardX2} y2={C.backboardY} stroke={G} strokeWidth="4" />
        <circle cx={C.hoopX} cy={C.hoopY} r={C.hoopR} fill="none" stroke={G} strokeWidth="3" />
        <path d={`M ${C.hoopX - C.restrictedR} ${C.hoopY} A ${C.restrictedR} ${C.restrictedR} 0 0 0 ${C.hoopX + C.restrictedR} ${C.hoopY}`} fill="none" stroke={G_FADE} strokeWidth="2" />
      </g>

      {/* === TOP HALF (opponent's end — mirrored: hoop at top) === */}
      <g transform={`translate(0, ${C.V}) scale(1, -1)`}>
        {/* Paint */}
        <rect x={C.paintX1} y={C.paintTopY} width={C.paintX2 - C.paintX1} height={C.paintBottomY - C.paintTopY} fill="none" stroke={G} strokeWidth="2.5" />
        <line x1={C.paintX1} y1={C.paintTopY} x2={C.paintX2} y2={C.paintTopY} stroke={G} strokeWidth="2.5" />
        <path d={`M ${C.ftCx - C.ftR} ${C.ftCy} A ${C.ftR} ${C.ftR} 0 0 1 ${C.ftCx + C.ftR} ${C.ftCy}`} fill="none" stroke={G} strokeWidth="2.5" />
        <path d={`M ${C.ftCx - C.ftR} ${C.ftCy} A ${C.ftR} ${C.ftR} 0 0 0 ${C.ftCx + C.ftR} ${C.ftCy}`} fill="none" stroke={G} strokeWidth="2" strokeDasharray="8 7" />
        <line x1={C.threeCornerX1} y1={C.paintBottomY} x2={C.threeCornerX1} y2={C.threeCornerY} stroke={G} strokeWidth="2.5" />
        <line x1={C.threeCornerX2} y1={C.paintBottomY} x2={C.threeCornerX2} y2={C.threeCornerY} stroke={G} strokeWidth="2.5" />
        <path d={`M ${C.threeCornerX1} ${C.threeCornerY} A ${C.threeR} ${C.threeR} 0 0 1 ${C.threeCornerX2} ${C.threeCornerY}`} fill="none" stroke={G} strokeWidth="2.5" />
        <line x1={C.backboardX1} y1={C.backboardY} x2={C.backboardX2} y2={C.backboardY} stroke={G} strokeWidth="4" />
        <circle cx={C.hoopX} cy={C.hoopY} r={C.hoopR} fill="none" stroke={G} strokeWidth="3" />
        <path d={`M ${C.hoopX - C.restrictedR} ${C.hoopY} A ${C.restrictedR} ${C.restrictedR} 0 0 0 ${C.hoopX + C.restrictedR} ${C.hoopY}`} fill="none" stroke={G_FADE} strokeWidth="2" />
      </g>

      {/* Center half-court line + jump circle */}
      <line x1={C.marginX} y1={C.V} x2={C.V - C.marginX} y2={C.V} stroke={G} strokeWidth="3" />
      <circle cx={C.V / 2} cy={C.V} r="120" fill="none" stroke={G} strokeWidth="2.5" />

      {/* Centre watermark */}
      <text x={C.V / 2} y={C.V + 10} textAnchor="middle"
        fontFamily="Oswald, sans-serif" fontSize="48" fill={G} opacity="0.05" fontWeight="700">
        PLAYMASTER
      </text>
    </g>
  );
}

export default function HalfCourtSVG({ children, className = '', viewBoxSize = 1000 }) {
  const V = viewBoxSize;
  return (
    <svg
      viewBox={`0 0 ${V} ${V}`}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      style={{ background: 'radial-gradient(ellipse at center top, #0E2440, #0A192F 70%)' }}
    >
      <CourtLines />
      {children}
    </svg>
  );
}
